import type { Container, Operation } from '@packages/core/container';

import { Ssm5Client } from '@packages/clients';

import type { Ssm5FreezeFramesResult } from '../types';

import { toFreezeFramesResult } from '../helpers/mappers';

/** Parameters of {@link GetFreezeFrames}. */
export type GetFreezeFramesParams = {
    /** The Cloud Connector ID of the open session. */
    cloudConnectorId: string;
    /** The trouble code whose freeze frame data is requested. */
    dtcCode: string;
    /** The module the DTC belongs to. */
    module: string;
    /** Aborts the read. */
    signal?: AbortSignal;
};

/** Read the freeze frame data of a DTC in an open SSM5 session. */
export class GetFreezeFrames implements Operation {
    /**
     * Register this operation with the SSM5 client as its dependency.
     *
     * @param container - the container to register in
     */
    static registerInContainer(container: Container): void {
        container.register(GetFreezeFrames, [Ssm5Client]);
    }

    /** @param client - the SSM5 API client */
    constructor(private client: Ssm5Client) {}

    /**
     * Run the read and settle it from its response.
     *
     * @param params - {@link GetFreezeFramesParams}
     * @returns the readout or whether its data is gone; rejects when aborted or when the read throws
     */
    async exec({ cloudConnectorId, dtcCode, module, signal }: GetFreezeFramesParams): Promise<Ssm5FreezeFramesResult> {
        const response = await this.client.getFreezeFrames({ cloudConnectorId, dtcCode, module }, signal);

        return toFreezeFramesResult(response);
    }
}
